/**
 * Agent Teams registry hooks: the admin Settings list plus its create, edit,
 * and delete mutations.
 *
 * Every mutation also invalidates the agent keys — deleting a team unassigns
 * its members Gateway-side, and a rename changes the `teamName` the Hub stamps
 * on each agent row, so a stale agent list would render the wrong sections.
 */

import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

export function useAgentTeamsQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.agentTeams(),
    queryFn: () => api.agentTeams.list(),
    enabled: options.enabled ?? true,
  });
}

export function useCreateAgentTeamMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; description?: string }) => api.agentTeams.create(payload),
    onSuccess: () => invalidateTeams(queryClient),
  });
}

export function useUpdateAgentTeamMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      agentTeamId: string;
      payload: { name?: string; description?: string };
    }) => api.agentTeams.update(input.agentTeamId, input.payload),
    onSuccess: () => invalidateTeams(queryClient),
  });
}

export function useDeleteAgentTeamMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentTeamId: string) => api.agentTeams.delete(agentTeamId),
    onSuccess: () => invalidateTeams(queryClient),
  });
}

function invalidateTeams(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.agentTeams() });
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.agents() });
}
